import { useCallback, use } from "react";
import { AlertManagerContext, AlertType, PromptType } from "../contexts/AlertProvider";
import { AlertButton, Keyboard, Platform } from "react-native";
import { Alert as DefaultAlert } from "react-native";
import ReactNativePromptAndroid from "../components/ReactNativePromptAndroid";

type AlertButtonWithoutLoginPassword = Omit<AlertButton, "onPress"> & {
    onPress?: (value?: string) => void;
};

type PromptTypeWithoutLoginPassword = Omit<PromptType, "callbackOrButtons"> & {
    callbackOrButtons: ((value: string) => void) | AlertButtonWithoutLoginPassword[];
};

export const useSetAlert = () => {
    const { setAlert, setPrompt } = use(AlertManagerContext);

    const _setAlert = useCallback(
        (alert: AlertType) => {
            Keyboard.dismiss();
            switch (Platform.OS) {
                case "web":
                    setAlert(alert);
                    break;
                default:
                    DefaultAlert.alert(alert.title, alert.message, alert.buttons, alert.options);
                    break;
            }
        },
        [setAlert],
    );

    const _setPrompt = useCallback(
        (prompt: PromptTypeWithoutLoginPassword) => {
            switch (Platform.OS) {
                case "ios":
                    // In some cases, the iOS keyboard unexpectedly closes immediately when the prompt open. Also happen when when tap on the field.
                    // This setTimout(() => { ... }, 0) could fixes this issue as it will open the prompt on next render
                    setTimeout(() => {
                        DefaultAlert.prompt(
                            prompt.title,
                            prompt.message,
                            prompt.callbackOrButtons,
                            prompt.type,
                            prompt.defaultValue,
                            prompt.keyboardType,
                        );
                    }, 0);
                    break;
                case "android":
                    // On Android we use a lib that implements the prompt dialog
                    ReactNativePromptAndroid(
                        prompt.title,
                        prompt.message,
                        prompt.callbackOrButtons,
                        {
                            type: prompt.type,
                            defaultValue: prompt.defaultValue,
                            cancelable: true,
                            style: "shimo",
                        },
                    );
                    break;
                case "web":
                    setPrompt(prompt);
                    break;
            }
        },
        [setPrompt],
    );

    return { setAlert: _setAlert, setPrompt: _setPrompt };
};
